import { useTranslation } from "react-i18next";
import { BindDialogReturn, DialogProps, DialogResult, DialogState, bindDialog, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { IReportNotificationSummaryDTO } from "../../services/reporting-data-service/ReportScheduleModels";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useForm } from "react-hook-form";
import { useEffect, type JSX } from "react";
import Form from "../../components/forms/form/Form";
import { List, ListItem } from "@mui/material";
import htmlParse from 'html-react-parser';
import { RestError } from "../../services/rest-service/RestService";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import ReportingDataService from "../../services/reporting-data-service/ReportingDataService";
import FormCheckbox from "../forms/form-checkbox/FormCheckbox";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";

export type ScheduledReportRecipientDialogInitValues = {
    groupID: number,
    taskID: number,
    recipients: IReportNotificationSummaryDTO[];
};

export type ScheduledReportRecipientDialogReturnValues = {
    
};

export type ScheduledReportRecipientDialogResult = DialogResult<ScheduledReportRecipientDialogReturnValues>;
export type ScheduledReportRecipientDialogProps = DialogProps<ScheduledReportRecipientDialogInitValues, ScheduledReportRecipientDialogResult>;

export type ScheduledReportRecipientDialogFormValuesType = {
    recipients: IReportNotificationSummaryDTO[]
};

const defaultFormValues: ScheduledReportRecipientDialogFormValuesType = {
    recipients: []
};

export default function ScheduledReportRecipientDialog({
    open,
    onClose,
    values,
}: ScheduledReportRecipientDialogProps): JSX.Element {

    const [,setAppNotificationState] = useAppNotificationContext();
    const { t } = useTranslation();
    const { acknowledge, confirm } = useConfirmation();

    const form = useForm<ScheduledReportRecipientDialogFormValuesType>({
        mode: "onChange",
        defaultValues: defaultFormValues
    });

    const {
        getValues,
        handleSubmit,
        formState: {
            isDirty,
            isValid
        },
        reset,
        watch,
        //trigger
    } = form;


    const recipients = watch('recipients');

    useEffect(() => {
        if (open) {

            if (values) {
                reset({
                    recipients: values?.recipients ?? []
                });
            }
        } else {
            reset(defaultFormValues);
        }
    }, [reset, values, open]);



    async function removeRecipients(items: IReportNotificationSummaryDTO[]): Promise<void> {
        try {

            await ReportingDataService.deleteReportDeliveries(values?.groupID as number,
                values?.taskID as number,
                items.map(o => o.id));

            acknowledge(t('scheduledReports.deleteRecipientsConfirmation.success'));

        } catch(error) {
            const err =  error as RestError;
                   
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                message: err.message,
                show: true,
                error: err
            });
        }
    }

    async function onOkay(_data: ScheduledReportRecipientDialogFormValuesType) {

        const formRecipients = getValues('recipients');

        const items = formRecipients.filter(o => o.selected);
        if (!items.length) {

            const result = await confirm({
                dialogTitle: t("scheduledReports.deleteRecipientsWarning.title"),
                confirmationBodyComponent: htmlParse(t("scheduledReports.deleteRecipientsWarning.confirmationHtml")) as JSX.Element,
                yesButtonText: t("buttons.okay"),
                showConfirmationText: true,
                showYesButton: true,
                showNoButton: false,
                showCloseButton: false
            });

            if (result === 'yes') {
                await removeRecipients(items);
            }
        }

        onClose && onClose({
            reason: "ok"
        });
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getCancelConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ reason: "cancel" });
        }
    }

    async function getCancelConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    return (
        <>
            <ResponsiveDialog
                title={t("actions.addRecipient")}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.okay")}
                open={open}
                onClose={
                    async (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }
                }
            >
               
                <Form form={form}>

                <List sx={{
                        paddingTop: 0,
                        overflowX: "hidden",
                        overflowY: "auto",
                        display: 'flex',
                        flexDirection: 'column',
                        flex: '1 1 auto'
                    }}>
                        {
                        recipients.map((o, index) => (

                            <ListItem key={index} dense>
                                <FormCheckbox
                                    name={`recipients.${index}.selected`}
                                    label={o.displayText}
                                    sx={{ 
                                        marginRight: 0,
                                        marginTop: 0,
                                    }}
                                    typographyProps={{
                                        sx: {
                                            fontSize: '.95rem',
                                        }
                                    }}
                                />

                            </ListItem>                   
                        ))
                        }
                    </List>


                </Form>
            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getCancelConfirmation}
            />
        </>
    );
}

export type ScheduledReportRecipientDialogState = DialogState<ScheduledReportRecipientDialogInitValues, ScheduledReportRecipientDialogResult>;
export type BindScheduledReportRecipientDialogReturn = BindDialogReturn<ScheduledReportRecipientDialogInitValues, ScheduledReportRecipientDialogResult>;

export function useScheduledReportRecipientDialogState(): ScheduledReportRecipientDialogState {
    return useDialogState();
}

export function bindScheduledReportRecipientDialog(props: ScheduledReportRecipientDialogState): BindScheduledReportRecipientDialogReturn {
    return bindDialog(props);
}
